import { apiClient } from '@/lib/api'
import { isNotFoundError } from '@/lib/api/error-message'
import {
  type AdditionalTax,
  type CreateAdditionalTaxRequest,
  type UpdateAdditionalTaxRequest,
} from '@/models/domain/additional-tax'
import {
  type Company,
  type CreateCompanyRequest,
  type UpdateCompanyRequest,
} from '@/models/domain/company'
import {
  type CreateDBARequest,
  type CreateDistrictRequest,
  type CreateLocationRequest,
  type CreateLocationTypeRequest,
  type CreatePriceLevelRequest,
  type DBA,
  type District,
  type Location,
  type LocationType,
  type SystemPriceLevel,
  type UpdateDBARequest,
  type UpdateDistrictRequest,
  type UpdateLocationRequest,
  type UpdateLocationTypeRequest,
  type UpdatePriceLevelRequest,
} from '@/models/domain/location'

export interface System {
  name: string
  display_name: string
  active: boolean
}

// api-core read/write path convention:
//   Reads  -> /core/v5/locations/{system}/...
//   Writes -> /core/v5/locations/admin/{system}/...
// Writes go through an /admin/ segment that requires a higher permission
// scope server-side. Both halves of every entity below follow this split.
class LocationService {
  async getLocations(system: string): Promise<Location[]> {
    return await apiClient.get<Location[]>(
      `/core/v5/locations/${encodeURIComponent(system)}/locations`
    )
  }

  async getLocation(system: string, id: number): Promise<Location> {
    return await apiClient.get<Location>(
      `/core/v5/locations/${encodeURIComponent(system)}/location/${String(id)}`
    )
  }

  async createLocation(system: string, request: CreateLocationRequest): Promise<Location> {
    return await apiClient.post<Location>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/locations`,
      request
    )
  }

  async updateLocation(
    system: string,
    id: number,
    request: UpdateLocationRequest
  ): Promise<Location> {
    return await apiClient.put<Location>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/location/${String(id)}`,
      request
    )
  }

  /** Returns true when no Location with this id exists for the system. */
  async isLocationIdAvailable(system: string, id: number): Promise<boolean> {
    try {
      await this.getLocation(system, id)
      return false
    } catch (err) {
      if (isNotFoundError(err)) {
        return true
      }
      throw err
    }
  }

  async getDBAs(system: string): Promise<DBA[]> {
    return await apiClient.get<DBA[]>(`/core/v5/locations/${encodeURIComponent(system)}/dbas`)
  }

  async getDBA(system: string, id: string): Promise<DBA> {
    return await apiClient.get<DBA>(
      `/core/v5/locations/${encodeURIComponent(system)}/dba/${encodeURIComponent(id)}`
    )
  }

  async createDBA(system: string, request: CreateDBARequest): Promise<DBA> {
    return await apiClient.post<DBA>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/dbas`,
      request
    )
  }

  async updateDBA(system: string, id: string, request: UpdateDBARequest): Promise<DBA> {
    return await apiClient.put<DBA>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/dba/${encodeURIComponent(id)}`,
      request
    )
  }

  async getDistricts(system: string): Promise<District[]> {
    return await apiClient.get<District[]>(
      `/core/v5/locations/${encodeURIComponent(system)}/districts`
    )
  }

  async getDistrict(system: string, id: string): Promise<District> {
    return await apiClient.get<District>(
      `/core/v5/locations/${encodeURIComponent(system)}/district/${encodeURIComponent(id)}`
    )
  }

  async createDistrict(system: string, request: CreateDistrictRequest): Promise<District> {
    return await apiClient.post<District>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/districts`,
      request
    )
  }

  async updateDistrict(
    system: string,
    id: string,
    request: UpdateDistrictRequest
  ): Promise<District> {
    return await apiClient.put<District>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/district/${encodeURIComponent(id)}`,
      request
    )
  }

  async getLocationTypes(system: string): Promise<LocationType[]> {
    return await apiClient.get<LocationType[]>(
      `/core/v5/locations/${encodeURIComponent(system)}/location-types`
    )
  }

  async getLocationType(system: string, id: string): Promise<LocationType> {
    return await apiClient.get<LocationType>(
      `/core/v5/locations/${encodeURIComponent(system)}/location-type/${encodeURIComponent(id)}`
    )
  }

  async createLocationType(
    system: string,
    request: CreateLocationTypeRequest
  ): Promise<LocationType> {
    return await apiClient.post<LocationType>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/location-types`,
      request
    )
  }

  async updateLocationType(
    system: string,
    id: string,
    request: UpdateLocationTypeRequest
  ): Promise<LocationType> {
    return await apiClient.put<LocationType>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/location-type/${encodeURIComponent(id)}`,
      request
    )
  }

  async getPriceLevels(system: string): Promise<SystemPriceLevel[]> {
    const response = await apiClient.get<SystemPriceLevel[]>(
      `/core/v5/locations/${encodeURIComponent(system)}/price-levels`
    )
    return Array.isArray(response) ? response : []
  }

  async getPriceLevel(system: string, name: string): Promise<SystemPriceLevel> {
    return await apiClient.get<SystemPriceLevel>(
      `/core/v5/locations/${encodeURIComponent(system)}/price-level/${encodeURIComponent(name)}`
    )
  }

  async createPriceLevel(
    system: string,
    request: CreatePriceLevelRequest
  ): Promise<SystemPriceLevel> {
    return await apiClient.post<SystemPriceLevel>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/price-levels`,
      request
    )
  }

  async updatePriceLevel(
    system: string,
    name: string,
    request: UpdatePriceLevelRequest
  ): Promise<SystemPriceLevel> {
    return await apiClient.put<SystemPriceLevel>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/price-level/${encodeURIComponent(name)}`,
      request
    )
  }

  async getCompanies(system: string): Promise<Company[]> {
    const response = await apiClient.get<Company[]>(
      `/core/v5/locations/${encodeURIComponent(system)}/companies`
    )
    return Array.isArray(response) ? response : []
  }

  async getCompany(system: string, id: string): Promise<Company> {
    return await apiClient.get<Company>(
      `/core/v5/locations/${encodeURIComponent(system)}/company/${encodeURIComponent(id)}`
    )
  }

  async createCompany(system: string, request: CreateCompanyRequest): Promise<Company> {
    return await apiClient.post<Company>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/companies`,
      request
    )
  }

  async updateCompany(system: string, id: string, request: UpdateCompanyRequest): Promise<Company> {
    return await apiClient.put<Company>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/company/${encodeURIComponent(id)}`,
      request
    )
  }

  async getAdditionalTaxes(system: string): Promise<AdditionalTax[]> {
    const response = await apiClient.get<AdditionalTax[]>(
      `/core/v5/locations/${encodeURIComponent(system)}/additional-taxes`
    )
    return Array.isArray(response) ? response : []
  }

  async getAdditionalTax(system: string, machineName: string): Promise<AdditionalTax> {
    return await apiClient.get<AdditionalTax>(
      `/core/v5/locations/${encodeURIComponent(system)}/additional-tax/${encodeURIComponent(machineName)}`
    )
  }

  async createAdditionalTax(
    system: string,
    request: CreateAdditionalTaxRequest
  ): Promise<AdditionalTax> {
    return await apiClient.post<AdditionalTax>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/additional-taxes`,
      request
    )
  }

  async updateAdditionalTax(
    system: string,
    machineName: string,
    request: UpdateAdditionalTaxRequest
  ): Promise<AdditionalTax> {
    return await apiClient.put<AdditionalTax>(
      `/core/v5/locations/admin/${encodeURIComponent(system)}/additional-tax/${encodeURIComponent(machineName)}`,
      request
    )
  }

  async getSystems(): Promise<System[]> {
    const response = await apiClient.get<{ systems?: System[]; total: number }>('/core/v5/systems')
    return response.systems ?? []
  }
}

export const locationService = new LocationService()
